import { Box, Typography } from '@mui/material';
// import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Card from '../common/Card';
import Checkbox from '../common/Checkbox';
import Dropdown from '../common/Dropdown';
import FormDatePicker from '../common/FormDatePicker';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Pill from '../common/Pill';
import TextArea from '../common/TextArea';
import { SectionHeading } from '../common/styled/SectionHeading';
import { MetaOption } from '../../types';

export interface FormValues {
  vessel: string;
  manager: number | null;
  supplier: number | null;
  port: string;
  pricelist: string;
  date: Date | string | any | null; // Date, string, or dayjs object, can be null
  poNumber: string;
  invoiced: boolean | undefined;
  invoiceNumber: string | undefined;
  remarks: string;
  salesTax: string;
  localTax: string;
  extraCosts: any[];
  [key: string]: any; // Allow dynamic extra cost fields
}

interface DeliveryFormProps {
  typeLabel?: string;
  deliveryType?: string;
  onSubmit: () => void;
  onCancel?: () => void;
  submitting?: boolean;
  extraCosts?: any[];
  dropdownOptions: {
    managers: MetaOption[];
    vessels: MetaOption[];
    suppliers: MetaOption[];
    ports: MetaOption[];
    pricelists: MetaOption[];
    extraCosts: MetaOption[];
    dates: any;
  };
  loadingStates: {
    managers: boolean;
    vessels: boolean;
    suppliers: boolean;
    ports: boolean;
    extraCosts: boolean;
  };
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  setValues: any;
  isAdmin?: boolean;
  isEditMode?: boolean;
}

const DeliveryForm: React.FC<DeliveryFormProps> = ({
  typeLabel,
  deliveryType,
  onSubmit,
  submitting = false,
  dropdownOptions,
  loadingStates,
  control,
  errors,
  setValues,
  isAdmin,
  isEditMode = false,
  onCancel: _onCancel,
  extraCosts: _extraCosts,
}) => {
  const { t } = useTranslation();
  // Use dropdown options and loading states from props (APIs are called in container)
  const managers = dropdownOptions.managers;
  const vessels = dropdownOptions.vessels;
  const suppliers = dropdownOptions.suppliers;
  const ports = dropdownOptions.ports;
  const extraCostsFromMeta = dropdownOptions.extraCosts;
  // const dates = dropdownOptions.dates;

  const managersLoading = loadingStates.managers;
  const vesselsLoading = loadingStates.vessels;
  const suppliersLoading = loadingStates.suppliers;
  const portsLoading = loadingStates.ports;
  const extraCostsLoading = loadingStates.extraCosts;

  const navigate = useNavigate();

  const invoiced = useWatch({ control, name: 'invoiced' });
  const selectedExtraCosts = (useWatch({ control, name: 'extraCosts' }) || []).filter(Boolean);

  const getError = (field: keyof FormValues) => {
    const error = errors[field];
    return error?.message as string;
  };

  const handleCancel = () => {
    navigate('/deliveries');
  };

  const handleFormSubmit = onSubmit;

  // Shared field components to avoid repetition across delivery types
  const pillSx = {
    maxWidth: { xs: '100%', sm: '500px' },
    minWidth: { xs: '120px', sm: '150px' },
    justifyContent: 'start' as const,
  };

  const renderTypeField = (label: string) => (
    <FormField label={t('form.type')}>
      <Pill sx={pillSx}>{label}</Pill>
    </FormField>
  );

  const renderManagerField = (fieldSx?: { mt?: number }) => (
    <FormField
      label={t('form.manager')}
      error={!!getError('manager')}
      helperText={getError('manager')}
      sx={{ mt: 3.5, ...fieldSx }}
      required
    >
      <Controller
        name="manager"
        control={control}
        rules={{ required: t('form.validation.managerRequired') }}
        render={({ field }) => (
          <Dropdown
            {...field}
            value={field?.value ?? ''}
            options={managers}
            placeholder={t('form.selectManager')}
            disabled={managersLoading || !isAdmin || isEditMode}
            hasSearch={true}
            debounceMs={500}
            maxDisplayedOptions={50}
            onChange={(e: any) => {
              field.onChange(e.target.value);
              setValues('vessel', '');
              setValues('supplier', '');
              setValues('date', '');
            }}
          />
        )}
      />
    </FormField>
  );

  const renderVesselField = (vesselSx?: object) => (
    <FormField
      label={t('form.vessel')}
      error={!!getError('vessel')}
      helperText={getError('vessel')}
      required
      sx={vesselSx}
    >
      <Controller
        name="vessel"
        control={control}
        rules={{ required: t('form.validation.vesselRequired') }}
        render={({ field }) => (
          <Dropdown
            {...field}
            value={field?.value ?? ''}
            options={vessels || []}
            placeholder={vesselsLoading ? t('common.loadingVessels') : t('form.selectVessel')}
            disabled={vesselsLoading || isEditMode}
            hasSearch={true}
            debounceMs={500}
            maxDisplayedOptions={50}
            onChange={(e: any) => {
              field.onChange(e.target.value);
              setValues('date', '');
            }}
          />
        )}
      />
    </FormField>
  );

  const renderDateField = () => (
    <FormField
      label={t('form.date')}
      error={!!getError('date')}
      helperText={getError('date')}
      required
    >
      <Controller
        name="date"
        control={control}
        rules={{ required: t('form.validation.dateRequired') }}
        render={({ field }) => (
          <FormDatePicker
            {...field}
            sx={{ width: '100% !important' }}
            disabled={isEditMode}
            // minDate={minDate}
            // maxDate={maxDate}
          />
        )}
      />
    </FormField>
  );

  const renderRemarksField = () => (
    <FormField
      label={t('form.remarks')}
      error={!!getError('remarks')}
      helperText={getError('remarks')}
    >
      <Controller
        name="remarks"
        control={control}
        rules={{}}
        render={({ field }) => (
          <TextArea
            {...field}
            value={field.value ?? ''}
            placeholder={t('form.remarksPlaceholder')}
          />
        )}
      />
    </FormField>
  );

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Card
        sx={{
          padding: { xs: '16px', sm: '20px', md: '24px' },
          height: '100%',
          position: 'relative',
          minHeight: '100%',
          pb: { xs: '80px', md: '24px' }, // Extra bottom padding on mobile for buttons
        }}
      >
        <Box sx={{ mb: { xs: 1, md: 1.5 }, mt: { xs: -1, md: -1.5 } }}>
          <SectionHeading>{t('deliveries.deliveryInformation')}</SectionHeading>
        </Box>

        <Box
          sx={(theme) => ({
            height: '2px',
            backgroundColor: (theme.palette as any).inputField.border,
            mx: { xs: '-16px', sm: '-20px', md: '-24px' },
            mb: { xs: 2, md: 3 },
          })}
        />

        <form onSubmit={handleFormSubmit}>
          {deliveryType === 'adjusting' ? (
            // Adjusting Delivery Form
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {renderTypeField(typeLabel || t('deliveries.adjustingDelivery'))}
                {renderManagerField()}
                {renderVesselField()}
                {renderDateField()}
              </Box>
              <Box sx={{ flex: 1 }}>{renderRemarksField()}</Box>
            </Box>
          ) : deliveryType === 'secondHand' ? (
            // 2nd Hand Vessel Acquisition Delivery Form
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {renderTypeField(typeLabel || t('deliveries.spPurchase'))}
                {renderManagerField()}
                {renderVesselField()}
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormField label={t('form.currency')}>
                  <InputField value={t('common.usd')} disabled onChange={() => {}} />
                </FormField>
              </Box>
            </Box>
          ) : (
            // Standard/New Building Delivery Form
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                {renderTypeField(typeLabel || t('deliveries.standardDelivery'))}
                {renderManagerField()}
                {renderVesselField({ mt: 1 })}
                {renderDateField()}
                {renderRemarksField()}
              </Box>

              {/* Right Column */}
              <Box sx={{ flex: 1 }}>
                <FormField label={t('form.currency')}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1.5, sm: 2 },
                      flex: 1,
                    }}
                  >
                    <Pill sx={{ width: { xs: '100%', sm: 'auto' } }}>{t('common.usd')}</Pill>

                    <Controller
                      name="poNumber"
                      control={control}
                      rules={{
                        pattern: {
                          value: /^[0-9]+$/,
                          message: 'PO Number can only contain numbers',
                        },
                      }}
                      render={({ field }) => (
                        <InputField
                          {...field}
                          value={field?.value ?? null}
                          placeholder={t('form.poNoPlaceholder')}
                          type="number"
                          error={!!getError('poNumber')}
                          helperText={getError('poNumber')}
                          // disabled={isEditMode}
                          sx={{
                            width: {
                              xs: '100%',
                              sm: !invoiced ? 150 : '100%',
                            },
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="invoiced"
                      control={control}
                      render={({ field }) => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          <Checkbox
                            checked={!!field?.value}
                            onChange={(_, checked) => field.onChange(checked)}
                            // disabled={isEditMode}
                          />
                          <Typography
                            sx={(theme) => ({
                              fontSize: { xs: '13px', sm: '14px' },
                              color: (theme.palette as any).formField.label,
                              whiteSpace: 'nowrap',
                            })}
                          >
                            {t('form.invoiced')}
                          </Typography>
                        </Box>
                      )}
                    />

                    {invoiced && (
                      <Controller
                        name="invoiceNumber"
                        control={control}
                        rules={
                          {
                            validate: (value: string, formValues: FormValues) => {
                              if (formValues.invoiced && (!value || value.trim() === '')) {
                                return t('form.validation.invoiceNumberRequired');
                              }
                              if (formValues.invoiced && value && !/^[0-9]+$/.test(value)) {
                                return t('form.validation.invoiceNumberNumbersOnly');
                              }
                              return true;
                            },
                          } as any
                        }
                        render={({ field }) => (
                          <InputField
                            {...field}
                            value={field?.value || ''}
                            type="number"
                            placeholder={t('form.invoiceNoPlaceholder')}
                            error={!!getError('invoiceNumber')}
                            helperText={getError('invoiceNumber')}
                          />
                        )}
                      />
                    )}
                  </Box>
                </FormField>

                <FormField
                  label={t('form.supplier')}
                  error={!!getError('supplier')}
                  helperText={getError('supplier')}
                  required
                >
                  <Controller
                    name="supplier"
                    control={control}
                    rules={{
                      required: t('form.validation.supplierRequired'),
                    }}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        value={field?.value ?? ''}
                        options={suppliers}
                        placeholder={
                          suppliersLoading ? t('common.loadingSuppliers') : t('form.selectSupplier')
                        }
                        disabled={suppliersLoading || isEditMode}
                        hasSearch={true}
                        debounceMs={500}
                        maxDisplayedOptions={50}
                      />
                    )}
                  />
                </FormField>

                <FormField
                  label={t('form.port')}
                  error={!!getError('port')}
                  helperText={getError('port')}
                  required
                >
                  <Controller
                    name="port"
                    control={control}
                    rules={{
                      required: t('form.validation.portRequired'),
                    }}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        value={field?.value ?? ''}
                        options={ports}
                        placeholder={portsLoading ? t('common.loadingPorts') : t('form.selectPort')}
                        disabled={portsLoading}
                        hasSearch={true}
                        debounceMs={500}
                        maxDisplayedOptions={50}
                      />
                    )}
                  />
                </FormField>

                {/* Extra Cost(s) */}
                <FormField
                  label={t('form.extraCosts')}
                  error={!!getError('extraCosts')}
                  helperText={getError('extraCosts')}
                  sx={{ mt: 1 }}
                >
                  {/* Vessel Dropdown */}
                  <Controller
                    name="extraCosts"
                    control={control}
                    rules={{}}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        value={field?.value ?? []}
                        options={extraCostsFromMeta}
                        placeholder={
                          extraCostsLoading
                            ? t('common.loadingExtraCosts')
                            : t('form.selectExtraCost')
                        }
                        disabled={extraCostsLoading}
                        hasSearch={true}
                        debounceMs={500}
                        maxDisplayedOptions={50}
                        multiSelect={true}
                      />
                    )}
                  />
                </FormField>

                {/* Sales Tax and Local Tax */}
                <FormField label="">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      flexWrap: 'wrap',
                      gap: { xs: 1.5, sm: 1 },
                      mb: { xs: 2, md: 3 },
                    }}
                  >
                    {selectedExtraCosts?.length > 0 &&
                      selectedExtraCosts?.filter(Boolean)?.map((selectedValue, index) => {
                        // Safety check to ensure selectedValue is not undefined
                        if (!selectedValue) return null;

                        // Find the corresponding extra cost option to get the label
                        const selectedOption = extraCostsFromMeta.find(
                          (option: MetaOption) => option.value === selectedValue,
                        );
                        const displayName = selectedOption?.label || selectedValue;
                        const fieldName =
                          selectedValue?.split('_')?.join('') || `extraCost_${index}`;

                        return (
                          <Controller
                            key={index}
                            name={fieldName}
                            control={control}
                            rules={{
                              validate: (val: any) => {
                                if (
                                  val === undefined ||
                                  val === null ||
                                  String(val).trim() === ''
                                ) {
                                  return t('form.validation.displayNameRequired', {
                                    name: displayName,
                                  });
                                }
                                return (
                                  /^[0-9]+\.?[0-9]*$/.test(String(val)) ||
                                  t('form.validation.displayNameNumbersOnly', { name: displayName })
                                );
                              },
                            }}
                            render={({ field }) => (
                              <InputField
                                {...field}
                                value={field?.value ?? ''}
                                placeholder={t('form.amountPlaceholder')}
                                type="number"
                                error={!!getError(fieldName)}
                                helperText={getError(fieldName)}
                                endAdornment={
                                  <Typography
                                    sx={(theme) => ({
                                      fontSize: {
                                        xs: '13px',
                                        sm: '14px',
                                        md: '15px',
                                      },
                                      color: (theme.palette as any).formField.helperDefault,
                                    })}
                                  >
                                    {t('common.usd')}
                                  </Typography>
                                }
                                sx={{
                                  width: {
                                    xs: '100%',
                                    sm: 'calc(50% - 4px)',
                                    md: '48%',
                                  },
                                }}
                                bottomLabel={displayName}
                              />
                            )}
                          />
                        );
                      })}
                  </Box>
                </FormField>
              </Box>
            </Box>
          )}
          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end',
              gap: { xs: 1.5, sm: 2 },
              mt: { xs: 3, md: 0 },
              position: { xs: 'static', md: 'absolute' },
              bottom: { md: '20px' },
              left: { md: 0 },
              right: { md: '20px' },
              px: { xs: 0, md: '20px' },
            }}
          >
            <Button variant="red" onClick={handleCancel} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {t('buttons.cancel')}
            </Button>
            <Button
              variant="outlinedBlue"
              type="submit"
              isLoading={submitting}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('buttons.proceed')}
            </Button>
          </Box>
        </form>
      </Card>
    </Box>
  );
};

export default React.memo(DeliveryForm);
